import { normalizeSnapshot } from '../persistence/schema';
import { loadRawSnapshot, putRecords } from '../persistence/v2Database';
import { applyAiJobsSnapshot, resetAiJobsStoreForTests } from './aiJobsStore';
import { applyAiSnapshot, resetAiStoreForTests } from './aiStore';
import { resetAreaFocusForTests } from './areaFocus';
import { applyEvidenceSnapshot, resetEvidenceStoreForTests } from './evidenceStore';
import { applyFindingSnapshot, resetFindingStoreForTests } from './findingStore';
import { applyInspectionSnapshot, resetInspectionStoreForTests } from './inspectionStore';
import { resetPoolAiStoreForTests } from './poolAiStore';
import { forgetPoolAiSpeedForTests } from './poolAiTiming';
import { applyReportSnapshot, resetReportStoreForTests } from './reportStore';
import { resetInspectorProfileStoreForTests } from './preferences';
import { forgetBoundLicenseMemory } from './boundLicense';
import { resetCommentLibraryForTests } from './commentLibrary';
import { hydrateRemoteSign, resetRemoteSignForTests } from './remoteSign';
import { resetSyncStatusForTests } from './syncStatus';

// Hydration: ONE read of the ACTIVE ACCOUNT's yanwushi-v2 database (src/persistence/accountScope.ts),
// validated by the schema authority, then fanned out to the stores. Nothing else is read — no legacy
// database, no legacy localStorage namespace (Guard 5 pins this), no other account's database.
// It runs when an account is entered (src/state/accountLifecycle.ts), never before one is known.
export interface HydrationReport {
  dropped: number;
  upgraded: number;
}

export async function hydrateAll(): Promise<HydrationReport> {
  const raw = await loadRawSnapshot();
  const snapshot = normalizeSnapshot(raw);
  if (snapshot.upgraded.length > 0) {
    // Explicit, declared V2→V2 upgrade / repair write-back (schema.ts). Never a legacy import.
    await putRecords(snapshot.upgraded.map((u) => ({ store: u.store, value: u.value })));
  }
  applyInspectionSnapshot({
    inspections: snapshot.inspections,
    areas: snapshot.areas,
    items: snapshot.items,
    dropped: snapshot.dropped,
    upgraded: snapshot.upgraded.length
  });
  applyEvidenceSnapshot(snapshot.evidence);
  applyFindingSnapshot(snapshot.findings);
  applyReportSnapshot(snapshot.reports);
  applyAiSnapshot(snapshot.aiRuns, snapshot.aiObservations);
  applyAiJobsSnapshot(snapshot.aiJobs);
  hydrateRemoteSign();
  return { dropped: snapshot.dropped, upgraded: snapshot.upgraded.length };
}

// Owner 2026-10-20: an inspection a file carried to this device was just written into THIS account's database
// (src/state/inspectionTransfer.ts). The stores read it back through the same validation as at sign-in.
export function reloadAccountData(): Promise<HydrationReport> {
  return hydrateAll();
}

// Everything the stores hold in MEMORY about the account that was active, dropped — nothing on disk is
// touched. Used when an account is left or another one is entered (account isolation 2026-09-21): the
// next inspector on this phone must not find the previous one's inspections, photos or profile in a
// store that was simply never emptied.
export function clearAccountStores(): void {
  resetInspectionStoreForTests();
  resetEvidenceStoreForTests();
  resetFindingStoreForTests();
  resetReportStoreForTests();
  resetAiStoreForTests();
  resetAiJobsStoreForTests();
  resetPoolAiStoreForTests();
  resetSyncStatusForTests();
  resetInspectorProfileStoreForTests();
  resetCommentLibraryForTests();
  resetRemoteSignForTests();
  forgetBoundLicenseMemory();
}

// Signed out: the stores are hydrated WITH NOTHING, so the shell stops waiting and shows Sign in.
export function applySignedOutState(): void {
  clearAccountStores();
  applyInspectionSnapshot({ inspections: [], areas: [], items: [], dropped: 0, upgraded: 0 });
  applyEvidenceSnapshot([]);
  applyFindingSnapshot([]);
  applyReportSnapshot([]);
  applyAiSnapshot([], []);
  applyAiJobsSnapshot([]);
}

export function resetAllStoresForTests(): void {
  clearAccountStores();
  resetAreaFocusForTests();
  forgetPoolAiSpeedForTests();
}
